import { z } from 'zod';
import { SiteSchema } from './site';
import { ElectricLoadSchema } from './load';
import { ElectricTariffSchema, GasTariffSchema } from './tariff';
import { PVSchema, ElectricStorageSchema, GeneratorSchema, CHPSchema } from './equipment';
import { FinancialSchema, SettingsSchema } from './financial';

/**
 * Main Scenario Schema
 *
 * This is the complete schema for a Site DNA optimization scenario.
 * It combines all component schemas and adds cross-field validation.
 */

export const ScenarioSchema = z.object({
  // Required components
  Site: SiteSchema,
  ElectricLoad: ElectricLoadSchema,
  ElectricTariff: ElectricTariffSchema,

  // Optional components
  GasTariff: GasTariffSchema.optional(),
  PV: PVSchema.optional(),
  ElectricStorage: ElectricStorageSchema.optional(),
  Generator: GeneratorSchema.optional(),
  CHP: CHPSchema.optional(),

  // Configuration
  Financial: FinancialSchema.default({}),
  Settings: SettingsSchema.default({}),

  // Metadata (frontend-only, stripped before solver)
  scenario_name: z.string().optional(),
  scenario_description: z.string().optional(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
}).refine(
  // If off-grid, must have some generation
  (scenario) => {
    if (scenario.Settings?.off_grid_flag) {
      const hasPV = scenario.PV && scenario.PV.max_kw > 0;
      const hasGen = scenario.Generator && scenario.Generator.max_kw > 0;
      const hasCHP = scenario.CHP && scenario.CHP.max_kw > 0;
      return hasPV || hasGen || hasCHP;
    }
    return true;
  },
  { message: 'Off-grid scenarios require at least one generation source (PV, Generator, or CHP)' }
).refine(
  // Battery without PV requires grid charging enabled
  (scenario) => {
    if (scenario.ElectricStorage && scenario.ElectricStorage.max_kwh > 0) {
      const hasPV = scenario.PV && scenario.PV.max_kw > 0;
      const canGridCharge = scenario.ElectricStorage.can_grid_charge !== false;
      return hasPV || canGridCharge;
    }
    return true;
  },
  { message: 'Battery without PV requires grid charging to be enabled' }
);

/**
 * Validate a scenario and return detailed results
 */
export const validateScenario = (scenario) => {
  const result = ScenarioSchema.safeParse(scenario);

  if (result.success) {
    return {
      valid: true,
      data: result.data,
      errors: [],
      warnings: getScenarioWarnings(result.data),
    };
  }

  return {
    valid: false,
    data: null,
    errors: result.error.issues.map(issue => ({
      path: issue.path.join('.'),
      message: issue.message,
    })),
    warnings: [],
  };
};

/**
 * Get warnings for valid scenarios (things that might be unintended)
 */
const getScenarioWarnings = (scenario) => {
  const warnings = [];

  // Check for very large systems
  if (scenario.PV?.max_kw > 10000) {
    warnings.push('PV system is very large (>10 MW) - verify this is intended');
  }
  if (scenario.ElectricStorage?.max_kwh > 40000) {
    warnings.push('Battery is very large (>40 MWh) - verify this is intended');
  }

  // Check for potential misconfigurations
  if (scenario.PV && scenario.PV.max_kw > 0 && scenario.PV.min_kw === scenario.PV.max_kw) {
    if (scenario.ElectricStorage && scenario.ElectricStorage.min_kw === 0) {
      warnings.push('PV is prescribed but battery is optimized - intentional mixed mode?');
    }
  }

  // Check tariff/load mismatch
  const loadPeak = scenario.ElectricLoad?.peak_kw || Math.max(...(scenario.ElectricLoad?.loads_kw || [0]));
  if (scenario.PV?.max_kw > loadPeak * 2) {
    warnings.push('PV capacity is >2x load peak - significant export expected');
  }

  return warnings;
};

/**
 * Create a minimal valid scenario for testing
 */
export const createMinimalScenario = (overrides = {}) => ({
  Site: {
    latitude: 34.0522,
    longitude: -118.2437,
    facility_type: 'commercial_office',
  },
  ElectricLoad: {
    loads_kw: Array(8760).fill(100),
    year: new Date().getFullYear(),
    critical_load_fraction: 0.5,
  },
  ElectricTariff: {
    type: 'blended',
    blended_annual_energy_rate: 0.15,
    blended_annual_demand_rate: 15,
    export_rate: 0.05,
  },
  Financial: {},
  Settings: {
    time_steps_per_hour: 4,
  },
  ...overrides,
});

export default ScenarioSchema;
